import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Queue-able search param updates using React state, then flush to the URL (without triggering a navigation) after
 * React batches them.
 *
 * We'd prefer to simply use setSearchParams(), but we need this for two reasons:
 * 1. This issue noted in the docs (https://reactrouter.com/api/hooks/useSearchParams):
 *
 *    The function callback version of setSearchParams does not support the queueing logic that React's setState implements.
 *    Multiple calls to setSearchParams in the same tick will not build on the prior value. If you need this behavior, you
 *    can use setState manually.
 *
 *    Without this, a subsequent change to the search params can revert the first change.
 *
 * 2. There is currently no way to setSearchParams() without triggering a navigation (see comments below).
 */
export function useQueuedSearchParams() {
    const [realSearchParams, _seeCommentBelow] = useSearchParams();

    // Keep a local, mutable copy of the search params
    const [queuedSearchParams, setQueuedSearchParams] = useState(() => new URLSearchParams(realSearchParams));

    const searchParamsString = useMemo(() => realSearchParams.toString(), [realSearchParams]);

    // If URL changes externally, refresh local copy
    useEffect(() => {
        setQueuedSearchParams(new URLSearchParams(searchParamsString));
    }, [searchParamsString]);

    // Queue one change (works with multiple calls in the same tick)
    const queueSearchParamsUpdate = (updater: (p: URLSearchParams) => void) => {
        setQueuedSearchParams(prev => {
            const next = new URLSearchParams(prev);
            updater(next);
            return next;
        });
    };

    // Automatically flush queued updates to the URL once per render
    useEffect(() => {
        // Compare with the ACTUAL URL, not realSearchParams from react-router.
        // Since we use history.replaceState (bypassing react-router), realSearchParams
        // can become stale and not reflect our previous updates.
        const actualUrlParams = new URLSearchParams(window.location.search);
        if (queuedSearchParams.toString() === actualUrlParams.toString()) return;

        // See https://github.com/remix-run/remix/discussions/7966 -- there is currently no way to
        // setSearchParams() without triggering a navigation.
        // realSetSearchParams(queued, { replace: true, navigation: false });
        // So we use history.replaceState instead, going behind react-router's back:
        const searchString = queuedSearchParams.toString();
        const hash = window.location.hash ?? '';
        const url = searchString
            ? `${window.location.pathname}?${searchString}${hash}`
            : `${window.location.pathname}${hash}`;
        window.history.replaceState(null, '', url);
    }, [queuedSearchParams]);

    return { searchParams: queuedSearchParams, queueSearchParamsUpdate };
}
